import React from 'react';
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  subMonths,
  subWeeks,
  subYears,
  getYear,
  startOfYear,
  endOfYear,
  isSameDay,
  addDays,
  isBefore,
  isAfter,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import tabStyles from '../../Common/styles/Tabs.module.scss';
import styles from './styles/ReportingGraph.module.scss';

import { OmaLummeApp, ConsumptionApp } from '../../../omaLummeContext';

import Button from '../../Common/Button';

import DatePicker from '../../Common/DatePicker';
import { graphDateStyle } from '../../../utils/consumption';

export const ReportingHeader: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = ConsumptionApp.useDispatch();

  const { basicData, selectedMeteringPoint } = OmaLummeApp.useState();
  const { activeFilter, startDate, endDate, latestDay, latestDayFetched } = ConsumptionApp.useState();

  const meteringpointContracts = basicData?.filter((mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId);

  let latestContract = meteringpointContracts && meteringpointContracts[0];
  let firstContract = latestContract;

  if (latestContract && meteringpointContracts && meteringpointContracts?.length > 1) {
    firstContract = meteringpointContracts.sort((a, b) => (isAfter(new Date(a.contractStartDate), new Date(b.contractStartDate)) ? 1 : -1))[0];

    latestContract.contractStartDate = firstContract.contractStartDate;
  }

  const today = startOfDay(new Date());

  const presets = [
    {
      text: t('consumption:consumption.presets_latestDay'),
      start: startOfDay(latestDay || new Date()),
      end: endOfDay(latestDay || new Date()),
      resolution: 'day',
    },
    {
      text: t('consumption:consumption.presets_lastWeek'),
      start: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      end: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      resolution: 'week',
    },
    {
      text: t('consumption:consumption.presets_lastMonth'),
      start: startOfMonth(subMonths(today, 1)),
      end: endOfMonth(subMonths(today, 1)),
      resolution: 'month',
    },
    {
      text: t('consumption:consumption.presets_last6months'),
      start: startOfMonth(subMonths(today, 6)),
      end: endOfMonth(subMonths(today, 1)),
      resolution: 'year',
    },
    {
      text: getYear(subYears(today, 2)).toString(),
      start: startOfYear(subYears(today, 2)),
      end: endOfYear(subYears(today, 2)),
      resolution: 'year',
    },
    {
      text: getYear(subYears(today, 1)).toString(),
      start: startOfYear(subYears(today, 1)),
      end: endOfYear(subYears(today, 1)),
      resolution: 'year',
    },
  ];

  const renderPresets = (
    <>
      {presets &&
        startDate &&
        endDate &&
        presets.map(({ text, start, end, resolution }, index) => {
          const isSelected = isSameDay(start, startDate) && isSameDay(end, endDate);

          // Do not show preset Latest day if data could not be found for this month (or last month if day of month < 4)
          // Do not show preset if preset startDate is older than metering point startDate
          // Do not show preset if preset startDate is newer than metering point endDate

          const endDay = latestContract?.contractEndDate ? new Date(latestContract.contractEndDate) : addDays(new Date(), 1);

          if (
            (index > 0 || (index === 0 && latestDayFetched)) &&
            latestContract &&
            start >= new Date(latestContract.contractStartDate) &&
            start <= endDay
          ) {
            return (
              <Button
                key={text}
                type="button"
                className={`${isSelected ? 'buttonSmall' : 'buttonWhiteSmall'}`}
                onClick={(): void => {
                  dispatch(['changeStartDate', start]);
                  dispatch(['changeEndDate', end]);
                  dispatch(['changeActiveFilter', resolution]);
                }}
              >
                <>{text}</>
              </Button>
            );
          }
          return false;
        })}
    </>
  );

  const lastContractEndDate = latestContract?.contractEndDate ? new Date(latestContract.contractEndDate) : new Date();

  const timeStart = (resolution: 'year' | 'month' | 'week' | 'day') => {
    const day = resolution === 'day' ? latestDay || new Date() : new Date();

    if (resolution === 'year') {
      return isBefore(lastContractEndDate, startOfYear(day)) ? startOfYear(lastContractEndDate) : startOfYear(day);
    }
    if (resolution === 'month') {
      return isBefore(lastContractEndDate, startOfMonth(day)) ? startOfMonth(lastContractEndDate) : startOfMonth(day);
    }
    if (resolution === 'week') {
      return isBefore(lastContractEndDate, startOfWeek(day))
        ? startOfWeek(lastContractEndDate, { weekStartsOn: 1 })
        : startOfWeek(day, { weekStartsOn: 1 });
    }
    if (resolution === 'day') {
      return isBefore(lastContractEndDate, startOfDay(day)) ? startOfDay(lastContractEndDate) : startOfDay(day);
    }
  };
  const timeEnd = (resolution: 'year' | 'month' | 'week' | 'day') => {
    const day = resolution === 'day' ? latestDay || new Date() : new Date();

    if (resolution === 'year') {
      return isBefore(lastContractEndDate, startOfYear(day)) ? endOfYear(lastContractEndDate) : endOfYear(day);
    }
    if (resolution === 'month') {
      return isBefore(lastContractEndDate, startOfMonth(day)) ? endOfMonth(lastContractEndDate) : endOfMonth(day);
    }
    if (resolution === 'week') {
      return isBefore(lastContractEndDate, startOfWeek(day, { weekStartsOn: 1 }))
        ? endOfWeek(lastContractEndDate, { weekStartsOn: 1 })
        : endOfWeek(day, { weekStartsOn: 1 });
    }
    if (resolution === 'day') {
      return isBefore(lastContractEndDate, startOfDay(day)) ? endOfDay(lastContractEndDate) : endOfDay(day);
    }
  };

  const resolutionTabs = [
    {
      label: t('consumption:consumption.reportingFilter-day'),
      key: 'day',
      start: timeStart('day'),
      end: timeEnd('day'),
    },
    {
      label: t('consumption:consumption.reportingFilter-week'),
      key: 'week',
      start: timeStart('week'),
      end: timeEnd('week'),
    },
    {
      label: t('consumption:consumption.reportingFilter-month'),
      key: 'month',
      start: timeStart('month'),
      end: timeEnd('month'),
    },
    {
      label: t('consumption:consumption.reportingFilter-year'),
      key: 'year',
      start: timeStart('year'),
      end: timeEnd('year'),
    },
  ];

  const datePickerFormat = () => {
    if (activeFilter === 'year') {
      return 'yyyy';
    }
    if (
      activeFilter === 'month' &&
      isSameDay(startOfMonth(startDate || new Date()), startOfMonth(endDate || new Date())) &&
      isSameDay(startDate || new Date(), startOfMonth(startDate || new Date())) &&
      isSameDay(endDate || new Date(), endOfMonth(startDate || new Date()))
    ) {
      return 'LLLL';
    }
    return 'd.M.';
  };

  return (
    <header className={styles.header}>
      <div>
        <div className={tabStyles.tabContainer}>
          {resolutionTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${tabStyles.tab} ${activeFilter === tab.key ? tabStyles.active : ''}`}
              onClick={(): void => {
                dispatch(['changeShowComparisonData', false]);
                dispatch(['changeActiveFilter', tab.key]);
                dispatch(['changeStartDate', tab.start]);
                dispatch(['changeEndDate', tab.end]);
                dispatch(['changeDataLoading', true]);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.datepickerWrapper}>
          <DatePicker
            prevNext
            type="range"
            dates={{
              startDate: startDate,
              setStartDate: (newDate: Date | undefined) => dispatch(['changeStartDate', newDate]),
              endDate: endDate,
              setEndDate: (newDate: Date | undefined) => dispatch(['changeEndDate', newDate]),
            }}
            limits={{ minDate: new Date(firstContract?.contractStartDate || today), maxDate: today }}
            resolution={{ resolution: activeFilter, setResolution: (newResolution: string) => dispatch(['changeActiveFilter', newResolution]) }}
            presets={renderPresets}
            contract={latestContract}
            graphDateStyle={graphDateStyle(startDate, endDate, activeFilter)}
            customFormat={datePickerFormat()}
            consumptionResolution
          />
        </div>
      </div>
    </header>
  );
};
